// Backtracking — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as heap.js: concept → internals →
// the pattern's sub-variants → errors → complexity → beginner drills → cheatsheet → playground.

import backtrackingDrills from './backtrackingDrills';
const drill = id => backtrackingDrills.find(d => d.id === id);

const backtracking = {
  key: 'Backtracking',
  tagline: 'Try a choice, explore everything it leads to, then undo it — the "explore, then erase" search behind every combinatorial problem.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Backtracking',
      type: 'text',
      body: [
        "Backtracking is DFS with a specific rhythm: at every step, make one choice, recursively explore everything that choice leads to, and then undo that choice before trying the next one. That undo step — restoring the exact state from before the choice was made — is what makes it \"backtracking\" rather than plain recursion.",
        "It's the standard technique for problems that ask for ALL possible arrangements, selections, or configurations satisfying some rule: every subset, every permutation, every way to place non-attacking queens on a board. The search explores a tree of decisions, and backtracking is simply the discipline of cleaning up after each branch before moving to the next.",
      ],
    },
    {
      id: 'why',
      title: 'Why Backtracking?',
      type: 'text',
      body: [
        "Many problems have no shortcut formula — the only way to find every subset, every arrangement, or every valid configuration is to actually try building them, one choice at a time. Backtracking is the systematic way to do that without writing a different nested loop for every possible input size.",
        "The other half of the value is PRUNING: the moment a partial choice can be proven to never lead anywhere valid (a running sum already exceeds the target, a queen would attack another), backtracking abandons that entire branch immediately — never wasting time building out the rest of a doomed path. This is what separates a well-written backtracking solution from a naive \"generate everything, then filter\" approach: the naive version still builds every bad answer before discarding it; backtracking with pruning often never builds it at all.",
      ],
    },
    {
      id: 'analogy',
      title: 'How Backtracking Works — Two Real-World Pictures',
      type: 'text',
      heading: 'A Maze Explorer With Chalk, and a Chess Player Abandoning a Bad Line',
      body: [
        "Picture someone exploring an unfamiliar maze, marking their path with chalk as they go. At every junction, they pick a direction and keep walking, drawing a chalk line behind them. The moment they hit a dead end, they don't start over from the entrance — they walk back along their OWN chalk trail, erasing it as they retreat, back to the last junction that still had an unexplored option, and try that instead. The chalk being erased on the way back IS backtracking: the state is restored to exactly what it was before that choice, so the next attempt starts clean.",
        "Now picture a chess player calculating several moves ahead. Partway through analyzing one specific sequence of moves, they realize it clearly loses material — no need to play the whole line out to checkmate to know it's bad. They abandon that entire line of thought immediately and go back to considering a different move at the point where they made the questionable choice. That's pruning: recognizing a branch is doomed and cutting it off early, rather than exhaustively working out every consequence of a choice already known to be bad.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing the Decision Tree',
      type: 'tree-diagram',
      body: [
        "Every backtracking search explores a tree of decisions, even though the code itself is just a recursive function — this is the SAME idea as a tree's structure, just built dynamically by the recursion instead of existing as data beforehand. Below is the complete decision tree for generating every subset of {1, 2}: at each level, one specific element is either skipped (L) or taken (R).",
        "Four leaves, four subsets: {}, {2}, {1}, {1,2} — every possible combination of \"skip or take\" for two elements, systematically explored.",
      ],
      demo: {
        tree: {
          val: 'decide: 1?',
          left: { val: 'decide: 2?', left: { val: '{ }', left: null, right: null }, right: { val: '{2}', left: null, right: null } },
          right: { val: 'decide: 2?', left: { val: '{1}', left: null, right: null }, right: { val: '{1,2}', left: null, right: null } },
        },
        note: 'L = skip this element, R = take it. Every root-to-leaf path is one complete decision sequence — one subset.',
      },
    },
    {
      id: 'template',
      title: 'The Choose / Explore / Un-Choose Template',
      type: 'code',
      code:
`void backtrack(/* current state */) {
    if (/* current state is a complete, valid answer */) {
        results.add(/* a COPY of the current state */);
        return;
    }
    for (/* each option available right now */) {
        // CHOOSE: make the option part of the current state
        path.add(option);

        // EXPLORE: recurse — try to complete the answer from here
        backtrack(/* updated state */);

        // UN-CHOOSE: undo the choice, restoring state for the next option
        path.remove(path.size() - 1);
    }
}`,
      note: 'Every backtracking solution in this entire pattern is a variation on these exact three steps. Learn this shape once, and every specific problem becomes "what are my choices here, and when is a state complete?"',
    },
    {
      id: 'subsetstemplate',
      title: 'Applied: Subsets (Include or Exclude Each Element)',
      type: 'code',
      code:
`void backtrack(int start, int[] nums, List<Integer> path, List<List<Integer>> result) {
    result.add(new ArrayList<>(path));   // every path IS a valid subset — no separate "complete" check
    for (int i = start; i < nums.length; i++) {
        path.add(nums[i]);                    // choose
        backtrack(i + 1, nums, path, result);  // explore
        path.remove(path.size() - 1);          // un-choose
    }
}`,
      note: 'Notice results.add() happens at the START of every call, not just at some "final" base case — in Subsets, EVERY partial path along the way is itself a complete, valid answer.',
    },
    {
      id: 'permutationstemplate',
      title: 'Applied: Permutations (Order Matters, No Repeats)',
      type: 'code',
      code:
`void backtrack(boolean[] used, int[] nums, List<Integer> path, List<List<Integer>> result) {
    if (path.size() == nums.length) {
        result.add(new ArrayList<>(path));
        return;
    }
    for (int i = 0; i < nums.length; i++) {
        if (used[i]) continue;                 // skip anything already in the path
        used[i] = true; path.add(nums[i]);      // choose
        backtrack(used, nums, path, result);    // explore
        used[i] = false; path.remove(path.size() - 1);  // un-choose
    }
}`,
      note: 'The difference from Subsets: every loop restarts from index 0 (order matters, and every element must eventually be tried in every position) — a used[] array is what stops an element from being picked twice in the same path.',
    },
    {
      id: 'pruning',
      title: 'Pruning: Cutting Off Doomed Branches Early',
      type: 'code',
      code:
`void backtrack(int start, int sum, int target, int[] candidates, List<Integer> path, List<List<Integer>> result) {
    if (sum == target) { result.add(new ArrayList<>(path)); return; }
    for (int i = start; i < candidates.length; i++) {
        if (sum + candidates[i] > target) continue;   // PRUNE — don't even recurse
        path.add(candidates[i]);
        backtrack(i, sum + candidates[i], target, candidates, path, result);
        path.remove(path.size() - 1);
    }
}`,
      note: 'The continue on the prune line is what makes this fast: without it, the code would still recurse one level deeper before discovering the sum is already too big — pruning checks BEFORE recursing, not after.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Forgetting the un-choose step',
          why: 'Without removing the choice from the path after exploring it, every subsequent sibling branch inherits leftover state from a choice that\'s no longer active — producing wrong or duplicated results.',
          fix: 'Every choose (path.add, marking used, incrementing a sum) needs a matching un-choose right after the recursive call returns — treat them as a pair that must always both execute.',
        },
        {
          mistake: 'Adding the path itself instead of a copy',
          why: 'results.add(path) stores a REFERENCE to the same mutable list — every future choose/un-choose after that point silently changes the "already recorded" result too, since it\'s the same object.',
          fix: 'Always add a copy: results.add(new ArrayList<>(path)), never the path object itself.',
        },
        {
          mistake: 'Pruning after recursing instead of before',
          why: 'Checking a condition (like sum exceeding target) only after making the recursive call still pays for entering that call and its own base-case check — the wasted work happens anyway, just one level deeper.',
          fix: 'Check the prune condition and continue (skip to the next option) BEFORE calling the recursive function, not inside it.',
        },
        {
          mistake: 'Reusing the wrong starting index for repetition rules',
          why: 'Passing i + 1 when repeats should be allowed (or i when they shouldn\'t) silently changes the problem being solved — Combination Sum needs i (reuse allowed), Subsets and Combinations need i + 1 (no reuse).',
          fix: 'Double-check the repetition rule the problem actually states, and match the recursive call\'s starting index to it deliberately.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'Subsets', time: 'O(2ⁿ)', note: 'Every element is either included or excluded — 2 choices, n elements.' },
        { op: 'Permutations', time: 'O(n · n!)', note: 'n! orderings, each taking O(n) to copy into the result.' },
        { op: 'Combinations (choose k of n)', time: 'O(k · C(n, k))', note: 'Far fewer than all subsets once results are restricted to exactly size k.' },
        { op: 'N-Queens', time: 'Well below O(n!) in practice', note: 'Conflict-checking prunes the vast majority of the naive n! placement attempts.' },
        { op: 'Space, in every variant above', time: 'O(n) — or O(depth) for grid problems', note: 'Bounded by recursion depth, not by how many results are eventually found.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for backtracking when you see…',
      body: [
        'A request for ALL possible subsets, permutations, combinations, or arrangements — not just one answer, or a count.',
        'A constraint-satisfaction problem: placing items (queens, colors) subject to rules that some placements immediately violate.',
        'A problem phrased as "find every way to..." over a small-to-moderate input size, where the answer set itself can be exponential.',
        'A partial solution that can be checked for invalidity BEFORE it\'s complete — that\'s exactly what makes pruning possible and worthwhile.',
        'Avoid it when only ONE optimal answer is needed and greedy or dynamic programming can find it directly — backtracking\'s exhaustive-search cost is only worth paying when genuinely all (or many) answers are required.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('generate-all-permutations'),
            drill('generate-all-combinations'),
            drill('subsets-drill'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('combination-sum-drill'),
            drill('generate-parentheses'),
            drill('letter-combinations-phone-number'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('n-queens-drill'),
            drill('palindrome-partitioning'),
            drill('rat-in-a-maze'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with the choose/explore/un-choose rhythm and pruning before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'The universal template: choose (add to path), explore (recurse), un-choose (remove from path) — every problem in this pattern is a variation on this.',
        'Always add a COPY of the path to results, never the path object itself.',
        'Prune BEFORE recursing, not after — check the condition and continue/skip immediately.',
        'Subsets and Combinations: recurse with i + 1 (no reuse). Combination Sum (with repetition): recurse with i (reuse allowed).',
        'Permutations need a used[] array, since every position restarts its loop from index 0 — order matters.',
        'A "complete" state check differs by problem: Subsets records every partial path; Permutations and N-Queens only record once the path reaches full length.',
        'Backtracking\'s cost is exponential in the worst case — pruning is what makes it practical, not a guarantee of polynomial time.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default backtracking;
